import { FC, useCallback } from 'react';

import { useRecoilState } from 'recoil';

import { MailIcon } from 'assets/icons';
import ConfirmationModal from 'components/ConfirmationModal/ConfirmationModal';
import { MinimizedDockItem } from 'components/MinimizedDock/MinimizedDock';
import SendEmailModal from 'pages/Salesforce/SendEmailModal';
import {
  defaultSendEmailState,
  sendEmailStateAtom
} from 'store/atoms/sendEmailAtom';
import { translate } from 'utils';

/**
 * App-shell host for the Salesforce Send Mail popup.
 * Keeps one draft alive across route changes via Recoil, so the user can
 * minimize it, browse other records and restore it. Opened via useOpenSendEmail.
 */
const SendEmailHost: FC = () => {
  const [state, setState] = useRecoilState(sendEmailStateAtom);
  const { session, minimized, pending } = state;

  const handleClose = useCallback(() => {
    setState(defaultSendEmailState);
  }, [setState]);

  const handleMinimize = useCallback(() => {
    setState((prev) => ({ ...prev, minimized: true }));
  }, [setState]);

  const handleRestore = useCallback(() => {
    setState((prev) => ({ ...prev, minimized: false }));
  }, [setState]);

  const handleConfirmReplace = useCallback(() => {
    setState((prev) => ({
      session: prev.pending,
      minimized: false,
      pending: null
    }));
  }, [setState]);

  // Keep the current draft and show it, so the user sees what they kept.
  const handleCancelReplace = useCallback(() => {
    setState((prev) => ({ ...prev, pending: null, minimized: false }));
  }, [setState]);

  if (!session) return null;

  const currentLabel = session.recordLabel || session.recipientEmail;

  return (
    <>
      <SendEmailModal
        key={session.key}
        open={!minimized && !pending}
        relatedRecordId={session.relatedRecordId}
        relatedEntityType={session.relatedEntityType}
        recipientId={session.recipientId}
        recipientEmail={session.recipientEmail}
        onClose={handleClose}
        onMinimize={handleMinimize}
      />

      {minimized && !pending && (
        <MinimizedDockItem>
          <button
            type='button'
            onClick={handleRestore}
            className='flex max-w-xs items-center gap-2 px-4 py-3 rounded-full bg-PRIMARY text-white shadow-lg hover:opacity-90 transition-opacity'
            aria-label={translate('salesforce.send_email.restore')}
            title={translate('salesforce.send_email.restore')}
          >
            <MailIcon className='h-4 w-4 flex-shrink-0 brightness-0 invert' />
            <span className='text-sm font-medium truncate'>
              {translate('salesforce.send_email.minimized_label', {
                label: currentLabel
              })}
            </span>
          </button>
        </MinimizedDockItem>
      )}

      <ConfirmationModal
        open={!!pending}
        title={translate('salesforce.send_email.discard_draft_title')}
        message={translate('salesforce.send_email.discard_draft_message', {
          label: currentLabel
        })}
        confirmLabel={translate('salesforce.send_email.discard_draft_confirm')}
        onConfirm={handleConfirmReplace}
        onCancel={handleCancelReplace}
      />
    </>
  );
};

export default SendEmailHost;
